import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { categories, type Category } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { Link } from 'react-router-dom';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  useScrollToTop();
  const { setMeta } = useDocumentMeta();
  const [activeTab, setActiveTab] = useState(slug || '');

  const currentCategory = categories.find((c: Category) => c.slug === (slug || ''));
  const categoryArticles = articles.filter((a) => a.category === (slug || ''));

  useEffect(() => {
    setActiveTab(slug || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const ogImage = "https://readdy.ai/api/search-image?query=Abstract%20warm%20elegant%20category%20page%20banner%20with%20soft%20cream%20and%20terracotta%20tones%2C%20minimal%20editorial%20design&width=1200&height=630&seq=og-category&orientation=landscape";

  useEffect(() => {
    if (currentCategory) {
      setMeta({
        title: currentCategory.name + ' - 精品一区精选内容',
        description: currentCategory.description,
        keywords: '精品一区,' + currentCategory.name + ',精品一区内容,精品内容推荐,高质量内容',
        canonicalUrl: 'https://jingpinyiqu.com/category/' + (slug || ''),
        ogImage,
        ogType: 'website',
        structuredData: {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebPage',
              name: currentCategory.name + ' - 精品一区',
              description: currentCategory.description,
              url: 'https://jingpinyiqu.com/category/' + (slug || ''),
              isPartOf: { '@type': 'WebSite', name: '精品一区' },
            },
          ],
        },
      });
    }
  }, [currentCategory, slug, setMeta]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的分类不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mt-6 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500">
                <i className={currentCategory.icon + ' text-xl'}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.articleCount} 篇精选内容</p>
              </div>
            </div>
            <p className="text-foreground-600 leading-relaxed max-w-3xl">
              {currentCategory.description}
            </p>
          </div>

          <div className="mb-8 -mx-1">
            <div className="flex flex-wrap items-center gap-2 px-1 py-1 bg-background-100 rounded-full inline-flex">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={'/category/' + cat.slug}
                  className={
                    'px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ' +
                    (activeTab === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200')
                  }
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-background-100 rounded-lg p-6 md:p-8">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章，敬请期待。</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}